import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { graphql } from 'react-relay';

import { useFirePostOfficeTrigger } from '@atlassian/post-office-context/post-office-trigger';
import { usePostOfficeRoute } from '@atlassian/post-office-context/store';

import type {
	CurrentPageTemplateData,
	TemplateXflowContext,
	TemplateXflowTwcEligibilityDecision,
} from '@post-office/growth-common/confluence-experiments-context';
import { CANONICAL_TEMPLATE_KEYS } from '@post-office/growth-common/confluence-experiments-context/template-xflow';

import { useConfluenceAnalyticsEvents } from '@confluence/analytics/entry-points/useConfluenceAnalyticsEvents.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- pre-existing apollo-relay interop usage
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { watchContentHeaderTemplateData } from '@confluence/content-header/entry-points/watchContentHeaderTemplateData.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { getPageDataFromUrl } from './getPageDataFromUrl.ts';
import { useTemplateXflowReminderTapTrait } from './useTemplateXflowReminderTapTrait.ts';
import type {
	useCurrentPageTemplateDataTwcEligibilityQuery$data,
	useCurrentPageTemplateDataTwcEligibilityQuery$variables,
} from './__generated__/useCurrentPageTemplateDataTwcEligibilityQuery.graphql.ts';

export const TEMPLATE_VIEW_TRIGGER_ID = 'twcg-2112-template-view-trigger';
export const TEMPLATE_APPLIED_TRIGGER_ID = 'twcg-2112-template-applied-trigger';

// Retain the registered analytics identifier; it no longer controls experiment enrollment.
const TEMPLATE_XFLOW_ANALYTICS_KEY = 'twcg-2112-twc-xflow-conf-pages-templates';

const CurrentPageTemplateTwcEligibilityQuery = graphql`
	query useCurrentPageTemplateDataTwcEligibilityQuery($cloudIds: [ID!]!) {
		tenantContexts(cloudIds: $cloudIds) {
			cloudId
			confluenceEntitlementInfo: entitlementInfo(hamsProductKey: "confluence.ondemand") {
				... on CommerceEntitlementInfoCcp {
					entitlement {
						... on CcpEntitlement {
							relatesFromEntitlements(relationshipTypes: "COLLECTION") {
								relationshipType
							}
						}
					}
				}
			}
		}
	}
`;

export type CanonicalTemplateKey =
	(typeof CANONICAL_TEMPLATE_KEYS)[keyof typeof CANONICAL_TEMPLATE_KEYS];

const canonicalTemplateKeyBySourceTemplateEntityId: Readonly<Record<string, CanonicalTemplateKey>> =
	{
		...Object.values(CANONICAL_TEMPLATE_KEYS).reduce<Record<string, CanonicalTemplateKey>>(
			(mapping, key) => ({ ...mapping, [key]: key }),
			{},
		),
		'ec8ecc7e-7e4b-4f8a-aa3f-a6a7f2df4ac9': CANONICAL_TEMPLATE_KEYS.MEETING_NOTES,
		'f7a9afa1-19c0-4558-abd4-f8060829f9fc': CANONICAL_TEMPLATE_KEYS.PROJECT_PLAN,
		'6873d89c-765f-4677-b370-048005c7470b': CANONICAL_TEMPLATE_KEYS.EOW_STATUS_REPORT,
		'7edaef4a-329f-47c9-abfe-7e241c9e17ba': CANONICAL_TEMPLATE_KEYS.DECISIONS,
		'be29e5eb-fbe7-4213-b16f-5fb25b13f2d8': CANONICAL_TEMPLATE_KEYS.REQUIREMENTS_RACI,
		'f877653e-87e1-4654-a915-3591b3dd0db3': CANONICAL_TEMPLATE_KEYS.BUSINESS_STATUS_UPDATE,
		'7c19a101-d13e-4d67-870b-279e6342c6bc': CANONICAL_TEMPLATE_KEYS.RECURRING_MEETING_MINUTES,
		'a2484be7-df58-4f17-a347-2fecb7b22bb9': CANONICAL_TEMPLATE_KEYS.PROJECT_STATUS,
	};

export const getCanonicalTemplateKey = (
	sourceTemplateEntityId: string | null | undefined,
): CanonicalTemplateKey | undefined =>
	sourceTemplateEntityId
		? canonicalTemplateKeyBySourceTemplateEntityId[sourceTemplateEntityId]
		: undefined;

const isPaidEdition = (edition: string | null | undefined) =>
	edition === 'STANDARD' || edition === 'PREMIUM';

type ActiveTemplateTrigger = 'view' | 'applied' | undefined;

type TemplateXflowEntryPoint = 'templateView' | 'templateApplied';

type DeriveTwcEligibilityDecisionArgs = {
	shouldCheck: boolean;
	cloudId: string | undefined;
	data: useCurrentPageTemplateDataTwcEligibilityQuery$data | undefined;
	loading: boolean;
	error: unknown;
};

export const deriveTwcEligibilityDecision = ({
	shouldCheck,
	cloudId,
	data,
	loading,
	error,
}: DeriveTwcEligibilityDecisionArgs): TemplateXflowTwcEligibilityDecision => {
	if (!shouldCheck) {
		return 'notRequested';
	}

	if (!cloudId) {
		return 'missing';
	}

	if (loading) {
		return 'loading';
	}

	if (error) {
		return 'failed';
	}

	const tenantContext = data?.tenantContexts?.find((context) => context?.cloudId === cloudId);
	const relatesFromEntitlements =
		tenantContext?.confluenceEntitlementInfo?.entitlement?.relatesFromEntitlements;

	if (!tenantContext || !relatesFromEntitlements) {
		return 'missing';
	}

	return relatesFromEntitlements.some(
		(relationship) => relationship?.relationshipType === 'COLLECTION',
	)
		? 'existingTwcCustomer'
		: 'eligible';
};

/**
 * Supplies the current template to Post Office consumers and owns both boolean trigger lifecycles.
 *
 * Template eligibility requires paid, licensed access and a resolved non-TWC entitlement.
 *
 * The cache-backed view path reflects creation provenance only. Applying a later template does not
 * persist sourceTemplateEntityId, so refresh can return the original key or no key at all.
 */
export const useCurrentPageTemplateData = (): TemplateXflowContext => {
	const { current: href } = usePostOfficeRoute();
	const { contentId } = getPageDataFromUrl(href ?? '');
	const { cloudId, edition, isLicensed } = useSessionData();
	const { createEvent } = useConfluenceAnalyticsEvents();
	const { fireTrigger: fireTemplateViewTrigger } = useFirePostOfficeTrigger({
		triggerId: TEMPLATE_VIEW_TRIGGER_ID,
	});
	const { fireTrigger: fireTemplateAppliedTrigger } = useFirePostOfficeTrigger({
		triggerId: TEMPLATE_APPLIED_TRIGGER_ID,
	});
	const [currentPageTemplateData, setCurrentPageTemplateData] = useState<CurrentPageTemplateData>({
		contentId,
		blueprintModuleCompleteKey: undefined,
		isEligible: false,
	});
	const [activeTemplateTrigger, setActiveTemplateTrigger] = useState<ActiveTemplateTrigger>();
	const [isTerminallyDismissed, setIsTerminallyDismissed] = useState(false);
	const eligibilityAnalyticsKeyRef = useRef<string>();
	const appliedTemplateContentIdRef = useRef<string | undefined>();
	const hasPaidLicensedAccess = isPaidEdition(edition) && isLicensed;
	const exposedCurrentPageTemplateData = useMemo<CurrentPageTemplateData>(
		() =>
			currentPageTemplateData.contentId === contentId
				? currentPageTemplateData
				: {
						contentId,
						blueprintModuleCompleteKey: undefined,
						isEligible: false,
					},
		[contentId, currentPageTemplateData],
	);
	const shouldCheckTwcEligibility =
		Boolean(contentId) &&
		hasPaidLicensedAccess &&
		exposedCurrentPageTemplateData.blueprintModuleCompleteKey !== undefined;
	const {
		data: twcEligibilityData,
		loading: twcEligibilityLoading,
		error: twcEligibilityError,
	} = useQueryInterop<
		useCurrentPageTemplateDataTwcEligibilityQuery$data,
		useCurrentPageTemplateDataTwcEligibilityQuery$variables
	>(CurrentPageTemplateTwcEligibilityQuery, {
		variables: { cloudIds: cloudId ? [cloudId] : [] },
		skip: !shouldCheckTwcEligibility || !cloudId,
		fetchPolicy: 'cache-first',
		interopApolloAggClient: true,
	});
	const twcEligibilityDecision = deriveTwcEligibilityDecision({
		shouldCheck: shouldCheckTwcEligibility,
		cloudId,
		data: twcEligibilityData,
		loading: twcEligibilityLoading,
		error: twcEligibilityError,
	});

	useEffect(() => {
		const blueprintModuleCompleteKey = exposedCurrentPageTemplateData.blueprintModuleCompleteKey;
		const entryPoint: TemplateXflowEntryPoint | undefined =
			activeTemplateTrigger === 'view'
				? 'templateView'
				: activeTemplateTrigger === 'applied'
					? 'templateApplied'
					: undefined;
		const hasResolvedTwcEligibility =
			twcEligibilityDecision !== 'notRequested' && twcEligibilityDecision !== 'loading';

		if (
			contentId &&
			blueprintModuleCompleteKey &&
			entryPoint &&
			hasPaidLicensedAccess &&
			hasResolvedTwcEligibility
		) {
			const analyticsKey = [
				contentId,
				blueprintModuleCompleteKey,
				entryPoint,
				twcEligibilityDecision,
			].join(':');

			if (eligibilityAnalyticsKeyRef.current !== analyticsKey) {
				eligibilityAnalyticsKeyRef.current = analyticsKey;
				createEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'decided',
						actionSubject: 'twcTemplateXflowEligibility',
						source: entryPoint,
						attributes: {
							blueprintModuleCompleteKey,
							entryPoint,
							experimentKey: TEMPLATE_XFLOW_ANALYTICS_KEY,
							hasPaidLicensedAccess,
							twcEligibilityDecision,
						},
					},
				}).fire();
			}
		}

		if (
			isTerminallyDismissed ||
			!contentId ||
			!blueprintModuleCompleteKey ||
			twcEligibilityDecision !== 'eligible'
		) {
			// oxlint-disable-next-line @atlassian/perf-linting/no-chain-state-updates -- pre-existing pattern
			setCurrentPageTemplateData((current) =>
				current.isEligible ? { ...current, isEligible: false } : current,
			);
			return;
		}

		setCurrentPageTemplateData((current) =>
			current.contentId === contentId &&
			current.blueprintModuleCompleteKey === blueprintModuleCompleteKey
				? { ...current, isEligible: true }
				: current,
		);
	}, [
		activeTemplateTrigger,
		contentId,
		createEvent,
		exposedCurrentPageTemplateData.blueprintModuleCompleteKey,
		hasPaidLicensedAccess,
		isTerminallyDismissed,
		twcEligibilityDecision,
	]);

	const { reminder: reminderState } = useTemplateXflowReminderTapTrait({
		enabled:
			!isTerminallyDismissed &&
			twcEligibilityDecision === 'eligible' &&
			exposedCurrentPageTemplateData.blueprintModuleCompleteKey !== undefined,
	});
	const isTemplateXflowSuppressed = isTerminallyDismissed || reminderState === 'dismissed';

	useEffect(() => {
		eligibilityAnalyticsKeyRef.current = undefined;
		if (appliedTemplateContentIdRef.current !== contentId) {
			appliedTemplateContentIdRef.current = undefined;
		}
		setCurrentPageTemplateData({
			contentId,
			blueprintModuleCompleteKey: undefined,
			isEligible: false,
		});
		setActiveTemplateTrigger(undefined);

		if (!contentId || !hasPaidLicensedAccess) {
			return;
		}

		const subscription = watchContentHeaderTemplateData(contentId, (sourceTemplateEntityId) => {
			if (appliedTemplateContentIdRef.current === contentId) {
				return;
			}

			const blueprintModuleCompleteKey = getCanonicalTemplateKey(sourceTemplateEntityId);
			setCurrentPageTemplateData({
				contentId,
				blueprintModuleCompleteKey,
				isEligible: false,
			});
			setActiveTemplateTrigger(blueprintModuleCompleteKey ? 'view' : undefined);
		});

		return () => {
			subscription.unsubscribe();
			fireTemplateViewTrigger({ isActive: false });
			fireTemplateAppliedTrigger({ isActive: false });
		};
	}, [contentId, fireTemplateAppliedTrigger, fireTemplateViewTrigger, hasPaidLicensedAccess]);

	useEffect(() => {
		fireTemplateViewTrigger({
			isActive:
				twcEligibilityDecision === 'eligible' &&
				activeTemplateTrigger === 'view' &&
				!isTemplateXflowSuppressed,
		});
		fireTemplateAppliedTrigger({
			isActive:
				twcEligibilityDecision === 'eligible' &&
				activeTemplateTrigger === 'applied' &&
				!isTemplateXflowSuppressed,
		});
	}, [
		activeTemplateTrigger,
		fireTemplateAppliedTrigger,
		fireTemplateViewTrigger,
		isTemplateXflowSuppressed,
		twcEligibilityDecision,
	]);

	const onTemplateApplied = useCallback(
		(blueprintModuleCompleteKey: string | null | undefined) => {
			if (!contentId || !hasPaidLicensedAccess) {
				setActiveTemplateTrigger(undefined);
				setCurrentPageTemplateData({
					contentId,
					blueprintModuleCompleteKey: undefined,
					isEligible: false,
				});
				return;
			}

			const canonicalTemplateKey = getCanonicalTemplateKey(blueprintModuleCompleteKey);
			appliedTemplateContentIdRef.current = contentId;
			setActiveTemplateTrigger(canonicalTemplateKey ? 'applied' : undefined);
			setCurrentPageTemplateData({
				contentId,
				blueprintModuleCompleteKey: canonicalTemplateKey,
				isEligible: false,
			});
		},
		[contentId, hasPaidLicensedAccess],
	);

	const dismissForSession = useCallback(() => {
		setIsTerminallyDismissed(true);
		fireTemplateViewTrigger({ isActive: false });
		fireTemplateAppliedTrigger({ isActive: false });
	}, [fireTemplateAppliedTrigger, fireTemplateViewTrigger]);

	return useMemo(
		() => ({
			currentPageTemplateData: exposedCurrentPageTemplateData,
			twcEligibilityDecision,
			reminderState,
			isTerminallyDismissed,
			dismissForSession,
			onTemplateApplied,
		}),
		[
			dismissForSession,
			exposedCurrentPageTemplateData,
			isTerminallyDismissed,
			onTemplateApplied,
			reminderState,
			twcEligibilityDecision,
		],
	);
};
